import type { PriceCatalogRow } from "../api/pricingService";

/**
 * 价目表的只读视图 —— 卡面文案从这里取数,**不再有第二份价表**。
 *
 * ★ 它替掉的是 `creditRates.ts` 里那批价格常量。那批常量是 ×10 时代写的,
 *   而除出图外的全部线路已改成 ×1.6 ⇒ 卡面对着用户整体高报约 6.25 倍,
 *   而没有任何一条判据会红(那套单测钉的是「常量 vs 写死的期望值」,两边都在前端,
 *   对「平台改价」这件事结构上失明)。
 */
export interface PriceBook {
  /** 表里有没有数据。false = 还没到货 / 取失败 —— 卡面此时**不许编一个数**。 */
  readonly ready: boolean;
  /** 单位单价;查不到返回 null(调用方必须能处理 null,不许兜一个假值)。 */
  unit(skuCode: string, variantKey?: string | null): number | null;
  /**
   * 单位单价的**不取整**读数(`creditsPerUnitExact`,如省流长口播 3.5;`unit()` 给的是取整上界 4)。
   * 卡面标「约 N 积分 / 秒」这类**单价**用它(0927 K4 I)。查不到 / 后端没下发这一列 → null,
   * **不兜 `unit()` 的整数上界** —— 一个说得斩钉截铁却高 14% 的数比不显示更糟。
   */
  unitExact(skuCode: string, variantKey?: string | null): number | null;
  /** 某 SKU 全部规格档的单价区间(用于「N~M 积分/秒」这种跨档说法)。 */
  range(skuCode: string): readonly [number, number] | null;
  /**
   * 按 **model_code + 后缀** 反查规格档 key(如 `doubao-seedance-2-0-fast-260128` + `720p`
   * → `seedance20fast_720p`)。查不到返回 null。
   *
   * ★ 为什么不让前端按命名规则拼:规格档 key 的命名(`seedance20fast_` 这种缩写)
   *   只存在于 seed 里,前端拼 = **又一处「同一个知识两个出处」**,而且拼错了是静默的
   *   (报价口会拒,但拒的形态是「未登记的规格」,看起来像后端缺行)。
   *   这里是**从价目表数据里反查**,与后端 `PricingService.variantKeyFor` 同款做法。
   */
  variantByModel(skuCode: string, modelCode: string, suffix: string): string | null;
  /**
   * 这个动作是不是免费的(`pricing_mode = FREE`)。查不到返回 null(= 还不知道)。
   *
   * ★ 为什么不让调用方拿 `unit() === 0` 判:0 和「免费」在屏幕上是两回事 ——
   *   用户看到「0 积分」会以为坏了,看到「免费」才知道是白送。而且 `unit()` 返 0 也可能是
   *   「取不到」被别处兜成了 0。免费必须由**服务端的 pricing_mode** 说了算。
   */
  isFree(skuCode: string): boolean | null;
  rows: readonly PriceCatalogRow[];
}

const keyOf = (sku: string, variant?: string | null) => `${sku} ${variant ?? ""}`;

export function priceBookFrom(rows: readonly PriceCatalogRow[]): PriceBook {
  const byKey = new Map<string, number>();
  const exactByKey = new Map<string, number>();
  const rowsBySku = new Map<string, PriceCatalogRow[]>();
  const bySku = new Map<string, number[]>();
  const freeBySku = new Map<string, boolean>();
  for (const r of rows) {
    freeBySku.set(r.skuCode, r.pricingMode === "FREE");
    rowsBySku.set(r.skuCode, [...(rowsBySku.get(r.skuCode) ?? []), r]);
    byKey.set(keyOf(r.skuCode, r.variantKey), r.creditsPerUnit);
    if (typeof r.creditsPerUnitExact === "number" && Number.isFinite(r.creditsPerUnitExact)) {
      exactByKey.set(keyOf(r.skuCode, r.variantKey), r.creditsPerUnitExact);
    }
    const list = bySku.get(r.skuCode) ?? [];
    list.push(r.creditsPerUnit);
    bySku.set(r.skuCode, list);
  }
  return {
    ready: rows.length > 0,
    rows,
    unit: (sku, variant) => byKey.get(keyOf(sku, variant)) ?? null,
    unitExact: (sku, variant) => exactByKey.get(keyOf(sku, variant)) ?? null,
    isFree: (sku) => freeBySku.get(sku) ?? null,
    variantByModel: (sku, modelCode, suffix) => {
      const want = suffix.trim().toUpperCase();
      const hit = (rowsBySku.get(sku) ?? []).filter(
        (r) =>
          r.modelCode === modelCode &&
          r.variantKey != null &&
          (r.variantKey.toUpperCase() === want || r.variantKey.toUpperCase().endsWith(`_${want}`))
      );
      // 查到多个 = 命名有歧义,宁可返 null 也不猜一个(与后端 variantKeyFor「查到多个不一致就抛」同口径)
      return hit.length === 1 ? hit[0].variantKey : null;
    },
    range: (sku) => {
      const list = bySku.get(sku);
      if (!list || list.length === 0) return null;
      return [Math.min(...list), Math.max(...list)] as const;
    },
  };
}

/**
 * 空表 —— 取数前 / 取失败时用。
 *
 * ★ 每个查询都返 null,`ready` 为 false。**故意没有兜底数字**:
 *   「显示一个可能过期的数」比「显示加载中」坏得多 —— 前者用户会信,后者用户会等。
 */
export const EMPTY_PRICE_BOOK: PriceBook = priceBookFrom([]);

/**
 * 出图 / 长图的规格档 key:`{provider小写}_{档位}`。
 *
 * ★ 这一条是**按命名规则拼**的,跟 {@link PriceBook.variantByModel} 那条不一样 ——
 *   因为出图的规格档是按**渠道**分的(`openai_2K`),而渠道名不是价目表的一个列,
 *   反查不出来。所以这里只能拼。
 * ★ 拼错了会怎样:`book.unit()` 查不到 → 卡面渲染骨架、报价口不发 ⇒ **失败是看得见的**
 *   (不是静默按错价算)。这是接受这处拼串的前提。
 */
export function imageVariant(provider: string, tier: string): string {
  return `${provider.trim().toLowerCase()}_${tier.trim().toUpperCase()}`;
}

/** 平台 SKU 码(与 `xb_price_sku.sku_code` 逐字一致)。 */
export const SKU = {
  posterImage: "poster.image",
  longImage: "longimage.render",
  tryon: "tryon.image",
  video: "video.render",
  /**
   * 视频台本图(每镜首帧)—— 编排时出图的**结算 SKU**(ai 票 action_type=video_frame)。
   * ★0926 演示机实测:此前页面借 `poster.image/openai_2K` 报 66,实扣走本 SKU = 90/张(少报 36%)。
   *   首帧恒 2K 这件事由后端报价口自己认(`VideoFrameTierQuote`),前端只问 `default` 档,不拼档位。
   */
  videoFrame: "video.frame",
  dhRender: "dh.render",
  /** 声纹克隆(V16 0908 拍板:LIST/once,default 档 10 积分/次,enroll 成功后扣)。 */
  dhVoiceClone: "dh.voice_clone",
  /**
   * 爆款解析的**取片**那一步(V33 起独立 action_type `explosive_fetch`)。
   * LIST / once / 四档同价 —— 它是「拆这条」里**唯一确定**的那笔;
   * 同一动作的另一笔 `explosive.parse` 是 MARKUP/token,没有挂牌价。
   */
  explosiveFetch: "explosive.fetch",
  explosiveParse: "explosive.parse",
  /**
   * 追爆仓**换一个话题词**(0918 批次六,契约 §17.1)。一次换词 = videos+words+trend
   * 三口一并回源,所以它是一笔独立的对客价(三口原有 SKU 退成内部计价)。
   * ★ 取不到 ⇒ `null` ⇒ 面上写「按次计费」,**永不编数**(与取片价同一条口径)。
   */
  explosiveTopic: "explosive.discover.topic",
  reporterArticle: "reporter.article",
  geoProbe: "geo.probe",
  aiPolish: "ai.polish",
  posterTextAssist: "poster.text_assist",
  mapLeadSearch: "maplead.search",
  mapLeadAnalyze: "maplead.analyze",
  /** 素材照片 AI 补足(人物/产品共用 augment-panel,0903 专价,单价=每张)。 */
  packAugment: "pack.augment",
  /** 经营诊断(0903 专价,单价=每次)。 */
  advisorDiagnosis: "advisor.diagnosis",
  /**
   * 获客商机**补全联系方式**(0917 v2 契约 §12.2 把 display_name 从「解锁」改成「补全」)。
   * `unit='once'` ⇒ 不按量乘;V35 未落地前价目表里查不到,面上退「按次计费」不编数。
   */
  acqUnlock: "acq.unlock",
  /** 补全联系方式 · 企业库命中那一档(1015 V41,120 积分)。卡面价格区间的上沿。 */
  acqUnlockCompany: "acq.unlock.company",
  /**
   * 发现一次(契约 §12.2 的五个收费种类,V42 落价)。
   *
   * ★★ **这五个键是兜底,不是主出处**。目录页的价一律读接口给的 `priceCredits`
   *   (服务端从 PricingService 现读),因为同一个种类的价会随 provider 成本调,
   *   而价目表是整表缓存的。两个出处打架时以**卡自带的那个**为准 —— 与商机卡
   *   `unlockCredits` 优先于 `acq.unlock` 同一条口径。
   * ★ 两个都取不到 ⇒ `null` ⇒ 面上写「按次计费」,**永不编数**。
   */
  acqDiscoverTender: "acq.discover.tender_ccgp",
  acqDiscoverZhihu: "acq.discover.zhihu_question",
  acqDiscoverWeibo: "acq.discover.weibo_event",
  acqDiscoverDouyin: "acq.discover.douyin_comment",
  acqDiscoverHitech: "acq.discover.hitech_cert",
} as const;

/**
 * 发现种类 → SKU 码。`type` 是**开放枚举**(契约 §12.1),所以这里只能按规则拼。
 *
 * ★ 拼错了会怎样:`book.unit()` 查不到 → 返回 null → 面上写「按次计费」。
 *   **失败是看得见的**(不是静默按错价算),这是接受这处拼串的前提 ——
 *   与 {@link imageVariant} 同一条理由。
 * ★ 上面那五个常量与这个函数**必须对得上**:常量给 TS 一个能跳转的锚,
 *   函数管新种类。`features/acquisition/model/acqLabels.test.ts` 里有一条判据钉着两者一致
 *   —— 判据放在获客侧是因为**只有那边知道有哪些种类**。
 */
export function acqDiscoverSku(type: string): string {
  return `acq.discover.${type}`;
}

/**
 * 数字人各档的规格档 key(与 `xb_price_sku_variant.variant_key` 逐字一致)——**只给卡面单价用**。
 *
 * ★没有旗舰(照片生动版)键,是有意的(Z2 0926):生动版价由 api 一处定(0926 拍板固定
 *   omnihuman 168/秒),前端不另存一份映射;问价走 `useDhPriceVariants`(api `GET /digital-human/price-variants`)。
 *   这两档与 provider 无关:tts 恒 cosyvoice、video 火山 realman 已对齐 videoretalk 价
 *   (gscq-ai llm_client.py:517-521)。照片省流版(longform → liveportrait)1019 永久下架,键已删。
 */
export const DH_VARIANT = {
  video: "videoretalk",
  tts: "tts_audio",
} as const;

/**
 * 有价才出文案 —— 取不到价一律返回空串,由卡面渲染骨架。
 *
 * ★ 这个守卫不是形式:兜一个数(哪怕是上一版的数)等于把一个可能过期的价钱说成真的,
 *   而用户会信它。「显示加载中」用户会等,「显示一个错的数」用户会照着做决定。
 */
export function label(credits: number | null, fmt: (c: number) => string): string {
  return credits == null ? "" : fmt(credits);
}

/**
 * 不取整单价的显示:「3.5」「168」—— 最多一位小数,整数不带 .0(B2 #16 定的口径;0927 K4 I 起
 * hub 卡面与创建页同用这一份,数字全部来自后端 `creditsPerUnitExact`,前端不写价)。
 */
export function formatPerSecondRate(rate: number): string {
  const rounded = Math.round(rate * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}

/** 同上,两个价一起(如「N~M 积分/秒」)。任一取不到就整条不出。 */
export function labelRange(
  range: readonly [number, number] | null,
  fmt: (lo: number, hi: number) => string
): string {
  return range == null ? "" : fmt(range[0], range[1]);
}
